// app/javascript/controllers/organization_map_controller.js
import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

export default class extends Controller {
  static targets = ["map"]
  static values = {
    apiKey: String,
    latitude: Number,
    longitude: Number,
    organizationName: String,
    operationalAreas: Array
  }
  
  connect() {
    if (this.hasApiKeyValue) {
      this.initializeMap()
    }
  }

  initializeMap() {
    mapboxgl.accessToken = this.apiKeyValue
    
    // Points to include in bounds calculation
    const points = []
    
    // Default center (Djibouti)
    const defaultCenter = [42.5903, 11.8251]
    const defaultZoom = 7
    
    // Create the map
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: defaultCenter,
      zoom: defaultZoom
    })
    
    // Add navigation controls
    this.map.addControl(new mapboxgl.NavigationControl(), 'top-right')
    
    // Add headquarters marker if coordinates are available and valid
    if (this.hasLatitudeValue && this.hasLongitudeValue && 
        this.latitudeValue !== 0 && this.longitudeValue !== 0) {
      const hqCoordinates = [this.longitudeValue, this.latitudeValue]
      points.push(hqCoordinates)
      
      const hqElement = document.createElement('div')
      hqElement.className = 'headquarters-marker'
      hqElement.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="#FF5757" d="M12 0C7.6 0 4 3.6 4 8c0 4.1 7 16 8 16s8-11.9 8-16c0-4.4-3.6-8-8-8zm0 12c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"/></svg>'
      
      new mapboxgl.Marker(hqElement)
        .setLngLat(hqCoordinates)
        .setPopup(new mapboxgl.Popup({ offset: 25 })
          .setHTML(`<h3 class="font-semibold">${this.organizationNameValue}</h3><p class="text-sm">Siège principal</p>`))
        .addTo(this.map)
    }
    
    // Add operational areas if available
    if (this.hasOperationalAreasValue && this.operationalAreasValue.length > 0) {
      this.operationalAreasValue.forEach(area => {
        if (area.longitude && area.latitude && 
            area.longitude !== 0 && area.latitude !== 0) {
          const areaCoordinates = [area.longitude, area.latitude]
          points.push(areaCoordinates)
          
          // Create regular marker for operational areas
          new mapboxgl.Marker({ color: '#3FB1CE' })
            .setLngLat(areaCoordinates)
            .setPopup(new mapboxgl.Popup({ offset: 25 })
              .setHTML(`
                <h3 class="font-semibold">Zone opérationnelle</h3>
                <p class="text-sm">${area.admin1 || ''}</p>
                ${area.admin2 ? `<p class="text-sm">${area.admin2}</p>` : ''}
                ${area.intervention_sectors && area.intervention_sectors.length > 0 ? 
                  `<div class="mt-2">
                    <p class="text-xs font-medium">Secteurs d'intervention:</p>
                    <div class="flex flex-wrap gap-1 mt-1">
                      ${area.intervention_sectors.map(sector => 
                        `<span class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">${sector}</span>`
                      ).join('')}
                    </div>
                  </div>` : ''
                }
              `))
            .addTo(this.map)
        }
      })
    }
    
    // Fit bounds to include all points if there are any
    if (points.length > 0) {
      const bounds = points.reduce((bounds, coord) => {
        return bounds.extend(coord)
      }, new mapboxgl.LngLatBounds(points[0], points[0]))
      
      this.map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 14
      })
    }
  }
}